import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  LANGUAGE_STORAGE_KEY,
  applyLanguageDocument,
  directionForLanguage,
  getStoredLanguage,
  normalizeLanguage,
  persistLanguage,
  translate,
} from "./catalogs";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => getStoredLanguage());

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    persistLanguage(normalized);
    applyLanguageDocument(normalized);
    return normalized;
  }, []);

  useEffect(() => {
    applyLanguageDocument(language);
  }, [language]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(normalizeLanguage(event.newValue));
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value = useMemo(() => ({
    language,
    direction: directionForLanguage(language),
    setLanguage,
    t: (key, variables) => translate(language, key, variables),
  }), [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
};

export { applyLanguageDocument, directionForLanguage, getStoredLanguage, normalizeLanguage, persistLanguage, translate } from "./catalogs";
